import { Component, type ReactNode, type ErrorInfo } from 'react';

import { isChunkLoadError, reloadForStaleBuild } from '@shared/lib/chunk-recovery';

import { ErrorFallback } from './ErrorFallback';

interface ModuleErrorBoundaryProps {
  children: ReactNode;
  /** Optional module name shown in console logs for easier debugging. */
  moduleName?: string;
}

interface State {
  error: Error | null;
}

/**
 * Error boundary that catches render errors in a module subtree and renders
 * `ErrorFallback` with a retry button instead of crashing the entire shell.
 *
 * Each module route in AppShell is wrapped with this boundary. When the user
 * navigates to a different route the boundary remounts (via AnimatePresence key),
 * automatically clearing the error state.
 *
 * @example
 * // In AppShell:
 * <ModuleErrorBoundary>
 *   <Outlet />
 * </ModuleErrorBoundary>
 */
export class ModuleErrorBoundary extends Component<ModuleErrorBoundaryProps, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const label = this.props.moduleName ?? 'unknown module';
    console.error(`[ModuleErrorBoundary] Render error in ${label}:`, error, info.componentStack);
  }

  reset = () => {
    if (isChunkLoadError(this.state.error)) {
      reloadForStaleBuild({ force: true });
      return;
    }
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      return <ErrorFallback error={this.state.error} resetErrorBoundary={this.reset} />;
    }
    return this.props.children;
  }
}
